import type { DocSection } from "./types";

export const DOCS: DocSection[] = [
  {
    id: "introduction",
    label: "Introduction",
    title: "Introduction",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "Consequence Protocol is an outcome verification layer. It is designed to measure what actions actually achieve — not merely whether they happened — and to transform verified consequences into reputation, trust, and economic access.",
      },
      {
        type: "p",
        text: "The protocol does not ask “did you perform the task?”. It asks “what happened because you performed it?”. That single shift turns activity into accountability: every claim can be evaluated against reality, and every verified result compounds into an actor’s track record.",
      },
      {
        type: "list",
        items: [
          "Proof of Consequence — the primitive this documentation describes.",
          "Outcome Contracts — how intent becomes measurable commitment.",
          "Verification — how evidence becomes verdicts.",
          "Consequence Score — how verdicts become reputation.",
        ],
      },
      {
        type: "note",
        tone: "info",
        text: "This documentation describes a protocol concept. Nothing here is deployed, audited, or live, and no figures represent real protocol statistics.",
      },
    ],
  },
  {
    id: "proof-of-consequence",
    label: "Proof of Consequence",
    title: "Proof of Consequence",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "Blockchains prove transactions. Social platforms prove attention. Neither proves that an action changed anything. Proof of Consequence is the missing primitive: cryptographic evidence that an action produced a measured, verifiable result in the world.",
      },
      { type: "h3", text: "The measure" },
      {
        type: "p",
        text: "The measure is not whether you acted. The measure is what happened because you acted. Ten thousand transactions, a hundred commits, or a million impressions prove nothing on their own — a verified delta against a stated baseline proves everything.",
      },
      { type: "h3", text: "The transformation" },
      {
        type: "code",
        lang: "plaintext",
        text: "ACTION → CLAIM → OUTCOME → EVIDENCE → VERIFICATION → CONSEQUENCE → REPUTATION",
      },
    ],
  },
  {
    id: "architecture",
    label: "Architecture",
    title: "Architecture",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "The protocol is organized as seven functional layers. Each layer consumes the output of the previous one, from raw action to compounding reputation.",
      },
      {
        type: "list",
        items: [
          "01 ACTION — intake: observable actions with a defined actor and intent.",
          "02 CLAIM — intent: measurable outcome commitments (Outcome Contracts).",
          "03 OUTCOME — measurement: the observable result a claim points at.",
          "04 EVIDENCE — proof: on-chain state, oracle feeds, attested reports.",
          "05 VERIFICATION — consensus: evidence evaluated against claim rules.",
          "06 CONSEQUENCE — settlement: measured deltas recorded permanently.",
          "07 REPUTATION — identity: consequences compound into track records.",
        ],
      },
      {
        type: "p",
        text: "The layers are deliberately independent: evidence sources are pluggable, verification methods are explicit per contract, and reputation is a read-only projection of settled consequences.",
      },
    ],
  },
  {
    id: "outcome-contracts",
    label: "Outcome Contracts",
    title: "Outcome Contracts",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "An Outcome Contract is the protocol’s atomic unit of intent. It binds an actor to a specific, measurable, time-boxed outcome before execution begins.",
      },
      {
        type: "code",
        lang: "typescript",
        text: `interface OutcomeContract {
  actor: Address;              // who is on the hook
  objective: string;           // "Increase liquidity"
  baseline: string;            // measured starting state
  target: string;              // the committed result
  measurement: string;         // how the delta is computed
  deadline: Epoch;             // when measurement closes
  evidenceSource: string[];    // oracles, chains, attestations
  verificationMethod: "on-chain" | "oracle" | "committee";
  stake: string;               // collateral posted against the claim
  reward: string;              // what verified success returns
  consequence: "reward" | "slash" | "reputation-only";
}`,
      },
      {
        type: "p",
        text: "Because the contract is falsifiable — baseline, target, measurement, and deadline are all explicit — verification can be deterministic. A claim that cannot be falsified cannot be verified, and is rejected at intake.",
      },
    ],
  },
  {
    id: "evidence",
    label: "Evidence",
    title: "Evidence",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "Evidence is the protocol’s raw material. Every Outcome Contract declares its acceptable evidence sources at creation, so actors and verifiers agree on what counts as proof before results exist.",
      },
      {
        type: "list",
        items: [
          "On-chain state — balances, positions, contract events.",
          "Oracle feeds — external market and environment data.",
          "Attested reports — signed security reviews, audit bundles.",
          "Computation proofs — verifiable off-chain computation outputs.",
        ],
      },
      {
        type: "note",
        tone: "warn",
        text: "Self-reported results are not evidence. Anything an actor alone controls must be corroborated by an independent declared source.",
      },
    ],
  },
  {
    id: "verification",
    label: "Verification",
    title: "Verification",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "Verification evaluates collected evidence against the claim’s measurement rules. Where the rules are deterministic — a revenue multiple, a gas benchmark — verification resolves automatically. Where judgment is required, a dispute window opens.",
      },
      {
        type: "code",
        lang: "plaintext",
        text: "$ consequence verify OC-0471\n\n> loading contract spec.......... [OK]\n> collecting evidence (3 sources). [OK]\n> evaluating measurement rules.... [OK]\n\n  baseline   $100,000\n  target     $115,000\n  actual     $127,400\n  delta      +27.4%\n\n  VERDICT    VERIFIED",
      },
      {
        type: "p",
        text: "Verdicts are binary at the contract level — VERIFIED or FAILED against the target — with CONFIRMED used for evidence-attested claims such as vulnerability disclosures.",
      },
    ],
  },
  {
    id: "consequence-score",
    label: "Consequence Score",
    title: "Consequence Score",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "The Consequence Score is a 0–100 projection of an actor’s settled history. It is derived from verified outcomes — never from raw activity volume — and weights impact magnitude, consistency, and dispute history.",
      },
      {
        type: "list",
        items: [
          "Impact — the measured delta of verified outcomes.",
          "Consistency — performance stability across categories.",
          "Recency — recent settled contracts weigh more.",
          "Disputes — unresolved disputes suppress the score.",
        ],
      },
      {
        type: "note",
        tone: "info",
        text: "Scores shown anywhere in this interface are simulated demonstrations, not live protocol metrics.",
      },
    ],
  },
  {
    id: "reputation",
    label: "Reputation",
    title: "Reputation",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "Reputation is the compounding asset the protocol produces. Every verified outcome extends an actor’s track record — a portable, evidence-backed history that markets can price and counterparties can trust.",
      },
      {
        type: "p",
        text: "Track records are append-only. Missed claims are recorded as honestly as verified ones, because a reputation without failures is a reputation without information.",
      },
    ],
  },
  {
    id: "disputes",
    label: "Disputes",
    title: "Disputes",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "When evidence is ambiguous or a measurement rule admits interpretation, any stakeholder can open a dispute during the contract’s dispute window. Disputed outcomes are flagged in the registry until resolved.",
      },
      {
        type: "list",
        items: [
          "Disputes freeze settlement of the affected contract.",
          "Resolution uses the contract’s declared verification method.",
          "Resolved disputes update the Consequence Score accordingly.",
        ],
      },
    ],
  },
  {
    id: "economics",
    label: "Economics",
    title: "Economics",
    updated: "TBD AT LAUNCH",
    blocks: [
      {
        type: "p",
        text: "Outcome Contracts may post stake against claims and return rewards on verified success, aligning incentives toward honest, measurable work. Dispute bonds discourage frivolous challenges.",
      },
      {
        type: "note",
        tone: "warn",
        text: "Tokenomics for $CONSEQ — supply, allocation, distribution, and emissions — have not been published. Nothing on this site constitutes economic data. Values will be announced at launch.",
      },
    ],
  },
  {
    id: "security",
    label: "Security",
    title: "Security",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "The protocol’s security model treats every surface as adversarial until evidenced: claims, evidence, and measurement rules are all explicit and inspectable.",
      },
      {
        type: "list",
        items: [
          "Deterministic verification wherever rules permit.",
          "Multi-source evidence requirements for high-value contracts.",
          "Dispute windows with bonded challenges.",
          "Append-only settlement — verdicts cannot be rewritten.",
        ],
      },
      {
        type: "note",
        tone: "warn",
        text: "No audits exist. Any future audit reports will be published directly and linked from this page.",
      },
    ],
  },
  {
    id: "roadmap",
    label: "Roadmap",
    title: "Roadmap",
    updated: "CONCEPT v1.0",
    blocks: [
      {
        type: "p",
        text: "The protocol is presented as a concept. Phases describe the intended order of work, not commitments or dates.",
      },
      {
        type: "list",
        items: [
          "PHASE 01 — Specification: Outcome Contract schema, evidence standards.",
          "PHASE 02 — Reference verification engine and registry interface.",
          "PHASE 03 — Dispute resolution and Consequence Score publication.",
          "PHASE 04 — Open ecosystem access for agents, protocols, and businesses.",
        ],
      },
    ],
  },
  {
    id: "faq",
    label: "FAQ",
    title: "Frequently Asked Questions",
    updated: "CONCEPT v1.0",
    blocks: [
      { type: "h3", text: "Is this live today?" },
      { type: "p", text: "No. Consequence Protocol is a concept. All interfaces, registries, and scores in this site are demonstrations." },
      { type: "h3", text: "What makes this different from analytics?" },
      { type: "p", text: "Analytics describe the past. Outcome Contracts bind intent in advance, so results can be verified against a committed baseline and target — turning measurement into accountability." },
      { type: "h3", text: "Who can build a track record?" },
      { type: "p", text: "Any actor whose actions are observable: AI agents, developers, traders, researchers, protocols, autonomous businesses, DAOs, and service providers." },
      { type: "h3", text: "What is $CONSEQ?" },
      { type: "p", text: "$CONSEQ is the network’s token on ARC. The contract is live — the official address, buy (lift.fun) and chart (DexScreener) venues are on the token page. Tokenomics will be published at launch." },
      { type: "h3", text: "Can I use this data?" },
      { type: "p", text: "Everything shown is demo or simulated. Do not treat any figure, score, or registry entry as real protocol statistics." },
    ],
  },
];
